// RD-SEC-061 / KAN-1041: the ONE reader of the server's sending-limit answer.
// Contract: HTTP 429 + { error: 'RATE_LIMITED', scope: 'company'|'user'|'ip', retryAfterSeconds }.
// Works on a parsed body, a callEdgeFunction result, a supabase-js invoke error
// (FunctionsHttpError: `context` is the Response, status only) or a thrown Error
// carrying `code` (raw-fetch PDF paths).

export const RATE_LIMITED_CODE = 'RATE_LIMITED';

function candidates(err) {
  if (!err || typeof err !== 'object') return [];
  return [err, err.error, err.body, err.data, err.details, err.context, err.context?.body]
    .filter((c) => c && typeof c === 'object');
}

export function isRateLimitedError(err) {
  if (err === RATE_LIMITED_CODE) return true;
  if (!err || typeof err !== 'object') return false;
  if (err.error === RATE_LIMITED_CODE || err.code === RATE_LIMITED_CODE) return true;
  if (candidates(err).some((c) => c.error === RATE_LIMITED_CODE || c.code === RATE_LIMITED_CODE)) return true;
  // FunctionsHttpError whose body has not been read: the Response status is all we have.
  return err.context?.status === 429 || err.status === 429;
}

export function rateLimitRetrySeconds(err) {
  for (const c of candidates(err)) {
    const n = Number(c.retryAfterSeconds);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/** Whole minutes to wait (rounded up, min 1), or null when the server gave no number. */
export function rateLimitRetryMinutes(err) {
  const s = rateLimitRetrySeconds(err);
  return s == null ? null : Math.max(1, Math.ceil(s / 60));
}

/** The user-safe sentence (no raw text). `intl.formatMessage` only. */
export function rateLimitedMessage(err, intl) {
  const minutes = rateLimitRetryMinutes(err);
  return minutes == null
    ? intl.formatMessage({ id: 'errors.sendLimit.later', defaultMessage: "You've sent a lot in the last hour. Please try again later." })
    : intl.formatMessage(
      { id: 'errors.sendLimit.minutes', defaultMessage: "You've sent a lot in the last hour. Please try again in about {minutes} minutes." },
      { minutes },
    );
}

/** True only for the sending-limit shape (scope/retry present or HTTP 429), not any legacy RATE_LIMITED. */
export function isSendingLimitError(err) {
  if (!isRateLimitedError(err)) return false;
  if (err?.context?.status === 429 || err?.status === 429) return true;
  return candidates(err).some((c) => c.scope != null || c.retryAfterSeconds != null);
}

/** Build a throwable from a raw-fetch 429 body so userSafeErrorMessage can map it. */
export function rateLimitedError(body) {
  const e = new Error('Sending limit reached');
  e.code = RATE_LIMITED_CODE;
  e.status = 429; // always the sending-limit shape, even when the body was unreadable
  e.scope = body?.scope;
  e.retryAfterSeconds = body?.retryAfterSeconds;
  return e;
}
